/**
 * Hazard Model
 *
 * Represents an individual hazard within a risk assessment.
 * Includes risk scoring, controls, and ALARP status.
 *
 * @class Hazard
 */

export class Hazard {

  /**
   * Create a new Hazard
   * @param {Object} data - Initial data
   */
  constructor(data = {}) {
    // Primary key
    this.id = data.id || this.generateId();
    this.riskAssessmentId = data.riskAssessmentId || '';

    // Hazard description
    this.hazardDescription = data.hazardDescription || '';
    this.whoAffected = data.whoAffected || '';

    // Initial risk (before controls)
    this.initialLikelihood = data.initialLikelihood || 1;
    this.initialSeverity = data.initialSeverity || 1;
    this.initialRiskScore = this.calculateRiskScore(
      this.initialLikelihood,
      this.initialSeverity
    );

    // Existing controls
    this.existingControls = data.existingControls || '';
    this.controlHierarchyLevel = data.controlHierarchyLevel || 'ppe';
    this.additionalControls = data.additionalControls || '';

    // Residual risk (after controls)
    this.residualLikelihood = data.residualLikelihood || this.initialLikelihood;
    this.residualSeverity = data.residualSeverity || this.initialSeverity;
    this.residualRiskScore = this.calculateRiskScore(
      this.residualLikelihood,
      this.residualSeverity
    );

    // ALARP status
    this.alarpStatus = this.getAlarpStatus(this.residualRiskScore);

    // Action plan
    this.actionRequired = data.actionRequired || '';
    this.actionOwner = data.actionOwner || '';
    this.actionDeadline = data.actionDeadline || null;

    // Ordering
    this.sequenceNumber = data.sequenceNumber || 1;

    // Metadata
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }

  /**
   * Generate unique ID
   * Format: HAZ-{timestamp}-{random}
   * @returns {string} Generated ID
   */
  generateId() {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `HAZ-${timestamp}-${random}`;
  }

  /**
   * Calculate risk score from likelihood and severity
   * @param {number} likelihood - Likelihood value (1-5)
   * @param {number} severity - Severity value (1-5)
   * @returns {number} Risk score (1-25)
   */
  calculateRiskScore(likelihood, severity) {
    return likelihood * severity;
  }

  /**
   * Get ALARP status from risk score
   * @param {number} score - Risk score (1-25)
   * @returns {string} ALARP status (intolerable, alarp, acceptable)
   */
  getAlarpStatus(score) {
    if (score >= 20) return 'intolerable';
    if (score >= 6) return 'alarp';
    return 'acceptable';
  }

  /**
   * Get risk level from score
   * @param {number} score - Risk score (1-25)
   * @returns {string} Risk level (extreme, high, medium, low)
   */
  getRiskLevel(score) {
    if (score >= 20) return 'extreme';
    if (score >= 12) return 'high';
    if (score >= 6) return 'medium';
    return 'low';
  }

  /**
   * Update initial risk values
   * @param {number} likelihood - New likelihood (1-5)
   * @param {number} severity - New severity (1-5)
   */
  updateInitialRisk(likelihood, severity) {
    this.initialLikelihood = likelihood;
    this.initialSeverity = severity;
    this.initialRiskScore = this.calculateRiskScore(likelihood, severity);
    this.updatedAt = new Date();
  }

  /**
   * Update residual risk values
   * @param {number} likelihood - New likelihood (1-5)
   * @param {number} severity - New severity (1-5)
   */
  updateResidualRisk(likelihood, severity) {
    this.residualLikelihood = likelihood;
    this.residualSeverity = severity;
    this.residualRiskScore = this.calculateRiskScore(likelihood, severity);
    this.alarpStatus = this.getAlarpStatus(this.residualRiskScore);
    this.updatedAt = new Date();
  }

  /**
   * Get risk reduction metrics
   * @returns {Object} Risk reduction data
   */
  getRiskReduction() {
    const scoreDelta = this.initialRiskScore - this.residualRiskScore;
    const percentageReduction = this.initialRiskScore > 0
      ? (scoreDelta / this.initialRiskScore) * 100
      : 0;

    return {
      scoreDelta,
      percentageReduction: Math.round(percentageReduction),
      levelChange: {
        from: this.getRiskLevel(this.initialRiskScore),
        to: this.getRiskLevel(this.residualRiskScore)
      },
      likelihoodReduction: this.initialLikelihood - this.residualLikelihood,
      severityReduction: this.initialSeverity - this.residualSeverity
    };
  }

  /**
   * Check if controls are effective
   * @returns {boolean} True if residual risk is lower than initial
   */
  areControlsEffective() {
    return this.residualRiskScore < this.initialRiskScore;
  }

  /**
   * Check if action is required
   * @returns {boolean} True if action required
   */
  requiresAction() {
    // Intolerable: Always requires action
    if (this.alarpStatus === 'intolerable') return true;

    // ALARP: Requires action if none specified
    if (this.alarpStatus === 'alarp') {
      return !this.actionRequired || this.actionRequired.trim() === '';
    }

    // Acceptable: No action required
    return false;
  }

  /**
   * Get control hierarchy effectiveness ranking
   * @returns {number} Effectiveness score (1-5, higher is better)
   */
  getControlEffectiveness() {
    const rankings = {
      'elimination': 5,
      'substitution': 4,
      'engineering': 3,
      'administrative': 2,
      'ppe': 1
    };

    return rankings[this.controlHierarchyLevel] || 1;
  }

  /**
   * Validate hazard data
   * @returns {Object} Validation result { valid: boolean, errors: string[] }
   */
  validate() {
    const errors = [];

    // Required fields
    if (!this.hazardDescription || this.hazardDescription.trim() === '') {
      errors.push('Hazard description is required');
    }

    // Likelihood validation
    if (this.initialLikelihood < 1 || this.initialLikelihood > 5) {
      errors.push('Initial likelihood must be between 1 and 5');
    }

    if (this.residualLikelihood < 1 || this.residualLikelihood > 5) {
      errors.push('Residual likelihood must be between 1 and 5');
    }

    // Severity validation
    if (this.initialSeverity < 1 || this.initialSeverity > 5) {
      errors.push('Initial severity must be between 1 and 5');
    }

    if (this.residualSeverity < 1 || this.residualSeverity > 5) {
      errors.push('Residual severity must be between 1 and 5');
    }

    // Control hierarchy validation
    const validHierarchies = ['elimination', 'substitution', 'engineering', 'administrative', 'ppe'];
    if (!validHierarchies.includes(this.controlHierarchyLevel)) {
      errors.push('Invalid control hierarchy level');
    }

    // Action plan validation for ALARP/Intolerable
    if (this.alarpStatus === 'intolerable' && !this.actionRequired) {
      errors.push('Action plan required for intolerable risks');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Convert to JSON for database storage
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      id: this.id,
      riskAssessmentId: this.riskAssessmentId,
      hazardDescription: this.hazardDescription,
      whoAffected: this.whoAffected,
      initialLikelihood: this.initialLikelihood,
      initialSeverity: this.initialSeverity,
      initialRiskScore: this.initialRiskScore,
      existingControls: this.existingControls,
      controlHierarchyLevel: this.controlHierarchyLevel,
      additionalControls: this.additionalControls,
      residualLikelihood: this.residualLikelihood,
      residualSeverity: this.residualSeverity,
      residualRiskScore: this.residualRiskScore,
      alarpStatus: this.alarpStatus,
      actionRequired: this.actionRequired,
      actionOwner: this.actionOwner,
      actionDeadline: this.actionDeadline,
      sequenceNumber: this.sequenceNumber,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }

  /**
   * Create from JSON data
   * @param {Object} data - JSON data from database
   * @returns {Hazard} New instance
   */
  static fromJSON(data) {
    return new Hazard(data);
  }
}
